import { ky } from './ky';
import { ListResponse } from './common';
import { ProductDto } from './product';
import { OrderDto, PaymentStatus } from './order';
import { ShipmentDto, SettlementDto } from './brand-commerce';

/**
 * 운영자 브랜드 조회 DTO. 백엔드 `OperatorBrandDto`(commerce/data/CommerceAdminDto.kt)와 매칭된다.
 * 운영자는 브랜드 식별 정보만 조회하며 민감 정보는 포함하지 않는다.
 */
export interface OperatorBrandDto {
  id: number;
  name: string;
  nickname: string;
}

/**
 * 결제 DTO. 백엔드 `PaymentDto`(commerce/data/PaymentDto.kt)와 매칭된다.
 * 운영자 주문 상세 통합 조회에서 결제 상태 확인에 사용한다.
 */
export interface PaymentDto {
  id: number;
  orderId: number;
  buyerMemberId: number;
  sellerAccountId: number;
  status: PaymentStatus;
  amount: number;
  currencyCode: string;
  idempotencyKey: string;
  provider?: string | null;
  providerPaymentId?: string | null;
  requestedAt: string;
  approvedAt?: string | null;
  failedAt?: string | null;
  canceledAt?: string | null;
}

/**
 * 운영자 주문 상세 통합 조회 DTO. 백엔드 `OperatorOrderDetailDto`와 매칭된다.
 * 주문/결제/배송/정산을 한 번에 묶어 CS·분쟁 조사에 사용한다.
 */
export interface OperatorOrderDetailDto {
  order: OrderDto;
  payment?: PaymentDto | null;
  shipment?: ShipmentDto | null;
  settlement?: SettlementDto | null;
  settlements: SettlementDto[];
}

/**
 * 커머스 감사 로그 DTO. 백엔드 `CommerceAuditLogDto`(commerce/data/CommerceAuditDto.kt)와 매칭된다.
 * 상태 변경/민감정보 접근을 actor, action, target, before/after 기준으로 조회한다.
 */
export interface CommerceAuditLogDto {
  id: number;
  actorMemberId?: number | null;
  actorRole: string;
  actorAccountId?: number | null;
  sellerAccountId?: number | null;
  action: string;
  targetType: string;
  targetId: number;
  beforeSummary?: string | null;
  afterSummary?: string | null;
  reason?: string | null;
  sensitiveAccess: boolean;
  requestId?: string | null;
  occurredAt: string;
}

/**
 * 운영자 커머스 콘솔 API 클라이언트.
 * 백엔드 `OperatorCommerceAdminController`(/v1/commerce/operator)와 매칭된다.
 * 접근 권한은 서버에서 검증하며, 권한이 없으면 403을 반환한다.
 */
export default class OperatorCommerceService {
  /** 전체 상품 조회 (운영자) */
  async listProducts() {
    const response = await ky.get('v1/commerce/operator/products');
    return (await response.json()) as ListResponse<ProductDto>;
  }

  /** 전체 주문 조회 (운영자) */
  async listOrders() {
    const response = await ky.get('v1/commerce/operator/orders');
    return (await response.json()) as ListResponse<OrderDto>;
  }

  /**
   * 주문 상세 통합 조회.
   * `includeSensitive`가 true일 때만 민감 정보 원문이 내려오며, 서버가 접근 audit log를 남긴다.
   * 기본값(false)에서는 민감 정보가 masking되어 내려온다.
   */
  async getOrder(orderId: number, includeSensitive = false) {
    const response = await ky.get(`v1/commerce/operator/orders/${orderId}`, {
      searchParams: { includeSensitive },
    });
    return (await response.json()) as OperatorOrderDetailDto;
  }

  /** 브랜드 목록 조회 (운영자) */
  async listBrands() {
    const response = await ky.get('v1/commerce/operator/brands');
    return (await response.json()) as ListResponse<OperatorBrandDto>;
  }

  /** 감사 로그 목록 조회 */
  async listAuditLogs() {
    const response = await ky.get('v1/commerce/operator/audit-logs');
    return (await response.json()) as ListResponse<CommerceAuditLogDto>;
  }

  /** 감사 로그 상세 조회 */
  async getAuditLog(auditLogId: number) {
    const response = await ky.get(`v1/commerce/operator/audit-logs/${auditLogId}`);
    return (await response.json()) as CommerceAuditLogDto;
  }
}
